import { lookupProfilePhoto } from './profilePhotos.js';
import { getLocalSettings } from './settingsStore.js';

/** Client-side helpers for profile photos shown on account and people avatars. */

export const PROFILE_AVATAR_MAX_BYTES = 80_000;
export const PROFILE_AVATAR_SIZE = 160;
export const PROFILE_AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read that image.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load that image.'));
    img.src = src;
  });
}

/**
 * Crop to a centered square and encode as a compact JPEG data URL.
 * @returns {Promise<string>}
 */
export async function fileToAvatarDataUrl(file, { size = PROFILE_AVATAR_SIZE } = {}) {
  if (!file) throw new Error('Choose a photo to upload.');
  if (!String(file.type || '').startsWith('image/')) {
    throw new Error('Use a JPG, PNG, WebP, or GIF photo.');
  }
  if (file.size > 8_000_000) {
    throw new Error('Choose a photo smaller than 8 MB.');
  }

  const rawUrl = await readFileAsDataUrl(file);
  const img = await loadImage(rawUrl);
  const side = Math.min(img.naturalWidth || img.width, img.naturalHeight || img.height);
  if (!side) throw new Error('That image could not be processed.');

  const sx = Math.floor(((img.naturalWidth || img.width) - side) / 2);
  const sy = Math.floor(((img.naturalHeight || img.height) - side) / 2);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process that image.');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);

  let quality = 0.82;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);
  while (dataUrl.length > PROFILE_AVATAR_MAX_BYTES && quality > 0.45) {
    quality -= 0.1;
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }
  if (dataUrl.length > PROFILE_AVATAR_MAX_BYTES) {
    throw new Error('That photo is still too large after compression. Try another image.');
  }
  return dataUrl;
}

export function isAvatarDataUrl(value) {
  return /^data:image\/(jpeg|jpg|png|webp|gif);base64,/i.test(String(value || ''));
}

/**
 * Photo for a person on the Users & Roles list.
 * Uses a photo stored on that person, then the signed-in account photo when
 * the emails match, then the profile saved for that email.
 */
export function orgUserProfilePhoto(person, account = {}, lookup = getLocalSettings) {
  const own = String(person?.avatarDataUrl || '').trim();
  if (own) return own;
  const email = String(person?.email || '').trim().toLowerCase();
  const accountEmail = String(account.email || '').trim().toLowerCase();
  const accountPhoto = String(account.avatarDataUrl || '').trim();
  if (email && accountEmail && email === accountEmail && accountPhoto) return accountPhoto;
  if (!email) return '';
  const saved = String(lookup(email)?.avatarDataUrl || '').trim();
  if (saved) return saved;
  return lookupProfilePhoto(email);
}
